import { useEffect } from "react";
import { NavLink } from "react-router-dom";

import autoCables from "../props/products/sae-auto-cables-1.jpg";
import batteryCableSet from "../props/products/sae-battery-cable-set-1.jpg";
import connectors from "../props/products/sae-connectors-1.jpg";
import fuses from "../props/products/sae-fuses-1.jpg";
import flexiblePipe from "../props/products/sae-flex-pipe-1.jpg";
import relayWiring from "../props/products/sae-relay-wiring-1.jpg";
import fuseBox from "../props/products/sae-fuse-box-1.png";

import { appConfig } from "../config/appConfig";
import { Navbar, ScrollToTop } from "../components";
import { createSlugForTableName } from "../helpers/inputSanitation.js";
import { useCatalogContext } from "../contexts/CatalogContextProvider.js";

const Home = () => {
  const { getAllActiveTables, activeTables } = useCatalogContext();

  useEffect(() => {
    getAllActiveTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productGallery = [
    {
      src: autoCables,
      alt: "Auto Cables",
      description:
        "High-quality automotive wiring cables for all vehicle types",
    },
    {
      src: batteryCableSet,
      alt: "Battery Cable Set",
      description: "Complete battery cable solutions with premium connectors",
    },
    {
      src: connectors,
      alt: "Electrical Connectors",
      description: "Durable connectors for reliable electrical connections",
    },
    {
      src: fuses,
      alt: "Automotive Fuses",
      description: "Circuit protection fuses for automotive applications",
    },
    {
      src: flexiblePipe,
      alt: "Flexible Conduit",
      description: "Protective conduit systems for wire harness management",
    },
    {
      src: relayWiring,
      alt: "Relay Wiring",
      description: "Professional relay wiring solutions and components",
    },
    {
      src: fuseBox,
      alt: "Fuse Box Systems",
      description: "Complete fuse box assemblies and distribution systems",
    },
  ];

  return (
    <div className="min-h-screen max-w-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 overflow-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="px-4 md:px-6 lg:px-8 py-16 md:py-20">
        <div className="container mx-auto text-center space-y-8">
          {/* Main Brand Header */}
          <div className="space-y-4">
            <div className="inline-block">
              <h1 className="bg-gradient-to-r from-themeColor1 via-themeColor2 to-themeColor1 text-4xl md:text-5xl lg:text-6xl font-bold text-transparent bg-clip-text leading-tight">
                Samson Auto Electricals
              </h1>
              <div className="h-1 w-full bg-gradient-to-r from-transparent via-themeColor1 to-transparent rounded-full mt-2"></div>
            </div>

            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto font-medium">
              Powering the automotive industry with premium auto-electrical solutions
              and innovative wiring systems
            </p>
          </div>

          {/* Tagline Badge */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-themeColor1/10 to-themeColor2/10 border border-themeColor1/20 px-6 py-3 rounded-full">
            <span className="text-2xl">⚡</span>
            <span className="text-gray-700 font-semibold">
              Electrifying the Auto-Industry
            </span>
          </div>

          {/* Dynamic Product Category Buttons */}
          {activeTables?.tables?.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4 pt-6">
              {activeTables.tables.map((table) => (
                <NavLink
                  to={`/products/${createSlugForTableName(table?.tableName)}`}
                  key={table?.id}
                  className="group"
                >
                  <button className="my-button-1 relative overflow-hidden group-hover:scale-105">
                    <span className="relative z-10">{table?.displayName}</span>
                  </button>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products Gallery Section */}
      <section className="px-4 md:px-6 lg:px-8 py-16 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-themeColor1 to-themeColor2 text-transparent bg-clip-text">
                Our Product Range
              </span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Discover our comprehensive collection of automotive electrical
              components, designed for reliability, performance, and
              professional installation.
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-themeColor1 to-themeColor2 rounded-full mx-auto mt-4"></div>
          </div>

          {/* Professional Image Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {productGallery.map((product, index) => (
              <div
                key={index}
                className="group my-smooth-transition-1 bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 hover:border-themeColor1/20 overflow-hidden"
              >
                {/* Image Container */}
                <div className="relative aspect-square overflow-hidden bg-gray-50">
                  <img
                    src={product.src}
                    alt={product.alt}
                    className="w-full h-full object-cover my-smooth-transition-1 group-hover:scale-110"
                  />

                  {/* Overlay on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 my-smooth-transition-1">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-white text-xs leading-relaxed">
                        {product.description}
                      </p>
                    </div>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 my-smooth-transition-1">
                    <span className="text-xs font-medium text-themeColor1">
                      {index + 1}
                    </span>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 text-sm md:text-base group-hover:text-themeColor1 my-smooth-transition-1">
                    {product.alt}
                  </h3>

                  {/* Quality Indicator */}
                  <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 my-smooth-transition-1">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-xs text-gray-500 font-medium">
                      Premium Quality
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mt-16 max-w-4xl mx-auto">
            <div className="text-center p-6 bg-white/80 rounded-xl border border-gray-100">
              <div className="w-12 h-12 border-4 border-themeColor2 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">⚡</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Premium Quality</h3>
              <p className="text-gray-600 text-sm">
                Industry-leading components built for durability and performance
              </p>
            </div>

            <div className="text-center p-6 bg-white/80 rounded-xl border border-gray-100">
              <div className="w-12 h-12 border-4 border-themeColor3 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">🔧</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Expert Solutions</h3>
              <p className="text-gray-600 text-sm">
                Professional-grade electrical systems for all automotive needs
              </p>
            </div>

            <div className="text-center p-6 bg-white/80 rounded-xl border border-gray-100">
              <div className="w-12 h-12 border-4 border-themeColor1 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">🚗</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Automotive Focus</h3>
              <p className="text-gray-600 text-sm">
                Specialized solutions designed specifically for the automotive
                industry
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 md:px-6 lg:px-8 py-16">
        <div className="container mx-auto text-center">
          <div className="bg-gradient-to-r from-white/80 to-blue-50/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-8 md:p-12 max-w-2xl mx-auto shadow-lg">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              Ready to Explore Our Solutions?
            </h3>
            <p className="text-gray-600 mb-8 text-lg">
              Visit our comprehensive website to discover our complete range of
              automotive electrical products and services.
            </p>

            <a href={appConfig.websiteUrl} target="_blank" rel="noreferrer">
              <button className="bg-gradient-to-r from-themeColor1 to-themeColor2 hover:from-themeColor2 hover:to-themeColor1 text-white px-8 py-4 font-bold text-lg rounded-xl shadow-lg hover:shadow-xl my-smooth-transition-1 transform hover:-translate-y-1">
                Visit Our Website
                <span className="ml-2 text-xl">↗</span>
              </button>
            </a>
          </div>
        </div>
      </section>
      <ScrollToTop />
    </div>
  );
};

export default Home;
